import { useState, useLayoutEffect } from "react";

import { useRouter } from "next/router";

import Article from "@/types/models/Article.interface";
import { Assignment } from "@/types/models/Assignment.interface";
import {
  IRecommendedReadingLevel,
  ILegacyStudentReadingLevel,
} from "@/types/models/ReadingLevel.interface";
import { User } from "@/types/models/User.interface";
import getUserProperties from "@/users/getUserProperties";
import getCurrentLevel from "@/utils/getCurrentLevel/index";
import contentTypes from "@/view/constants/contentTypes";

import useStudentQuizGet from "../useStudentQuizGet";

export interface IContent {
  data: Article;
  id: string;
  content_type: string;
}

const useLevelLock = (
  content: IContent,
  matchedAssignments: Assignment[],
  user: User,
  studentReadingLevel?: ILegacyStudentReadingLevel | IRecommendedReadingLevel,
) => {
  const { query } = useRouter();
  const contentId = Array.isArray(query["contentId"])
    ? query["contentId"]?.[0]
    : query["contentId"] || "";

  const { isStudent } = getUserProperties(user);

  const [isLevelLocked, setIsLevelLocked] = useState(false);

  const articleLevels = content?.data?.article_levels;
  const isArticleContentType =
    content?.data?.content_type === contentTypes.ARTICLE;
  const matchedAssignment = matchedAssignments?.[0];
  const isAssignmentLevelLocked =
    matchedAssignment?.is_reading_level_locked ||
    matchedAssignment?.grade_level_lock;
  const shouldLevelLockContent =
    isStudent && isAssignmentLevelLocked && isArticleContentType;
  const lockedLevel =
    shouldLevelLockContent &&
    getCurrentLevel({
      articleLevels,
      contentId: contentId || "",
      matchedAssignment,
      user,
      studentReadingLevel,
    });

  const { data: quiz } = useStudentQuizGet(lockedLevel?.id);
  const quizIsCompleted = quiz?.[0]?.answers?.length;

  // useLayoutEffect is used here to avoid level selector flickering
  useLayoutEffect(() => {
    if (quiz && !quizIsCompleted) {
      setIsLevelLocked(true);
    }
  }, [JSON.stringify(quiz), JSON.stringify(matchedAssignments)]);

  const unlockLevel = () => {
    setIsLevelLocked(false);
  };

  return { isLevelLocked, unlockLevel };
};

export default useLevelLock;
